/**
 * Substitutes the `{expectedTime}` placeholder in `message` with a localised, human-readable
 * delivery time. Falls back to the raw `deliveryTime` string when `deliveryTime` is not parseable
 * as a `Date`. When `message` is empty, returns the raw delivery time as-is.
 * Parses a two-date range only when `tempShowEddDateRange` is enabled.
 * @param message - Translation template containing `{expectedTime}`.
 * @param deliveryTime - ISO date string (or other parseable form) of the expected delivery moment.
 * @param options - Optional `Intl.DateTimeFormatOptions` applied to a date or date range.
 * @returns The substituted message ready to render to the user.
 * @category Composables
 */
export const useFormattedDeliveryDate = (
  message: string,
  deliveryTime: string,
  options?: Intl.DateTimeFormatOptions
) => {
  if (!message) return deliveryTime

  const { tempShowEddDateRange } = useFeatureFlags()
  let expectedTime: string
  const deliveryDateRange = tempShowEddDateRange ? useDeliveryDateRange(deliveryTime, options) : undefined

  if (deliveryDateRange) {
    expectedTime = deliveryDateRange
  }
  else {
    const trimmedDeliveryTime = deliveryTime?.trim()
    const parsedDate = new Date(trimmedDeliveryTime)
    expectedTime = parsedDate && !Number.isNaN(parsedDate.getTime())
      ? useDeliveryTime(parsedDate, options)
      : deliveryTime
  }

  return replaceAll(
    message,
    { expectedTime }
  )
}
